import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const navBgClass = !isHomePage
    ? 'bg-background-50'
    : scrolled
      ? 'bg-background-50'
      : 'bg-transparent';

  const textColorClass = !isHomePage
    ? 'text-foreground-950'
    : scrolled
      ? 'text-foreground-950'
      : 'text-white';

  const borderClass = !isHomePage
    ? 'border-foreground-200'
    : scrolled
      ? 'border-foreground-200'
      : 'border-white/40';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${scrolled ? 'py-2.5' : 'py-3.5'}`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between">
          {/* Left: Logo */}
          <Link
            to="/"
            className={`font-black font-heading text-lg md:text-xl whitespace-nowrap transition-colors duration-300 ${textColorClass}`}
          >
            Kakobuy Spreadsheet
          </Link>

          {/* Center: Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/"
              className={`h-9 px-4 rounded-full text-sm font-medium flex items-center transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isHomePage
                  ? (scrolled ? 'bg-accent-100 text-foreground-950' : 'bg-white/15 text-white')
                  : (scrolled ? 'text-foreground-700 hover:bg-foreground-100 hover:text-foreground-950' : 'text-white/80 hover:bg-white/15 hover:text-white')
              }`}
            >
              Home
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${dropdownOpen ? (scrolled ? 'bg-foreground-100 text-foreground-950' : 'bg-white/20 text-white') : (scrolled ? 'text-foreground-700 hover:bg-foreground-100 hover:text-foreground-950' : 'text-white/80 hover:bg-white/15 hover:text-white')}`}
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
              >
                Categories
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-xs"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  )}
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-60 bg-background-50 rounded-xl overflow-hidden z-50 max-h-[420px] overflow-y-auto border border-background-200" role="listbox">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-accent-100 hover:text-foreground-950 transition-colors duration-150 flex items-center gap-3 cursor-pointer whitespace-nowrap"
                      role="option"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`h-9 px-4 rounded-full text-sm font-medium flex items-center transition-all duration-200 whitespace-nowrap cursor-pointer ${scrolled ? 'text-foreground-700 hover:bg-foreground-100 hover:text-foreground-950' : 'text-white/80 hover:bg-white/15 hover:text-white'}`}
            >
              Shopping Guide
            </a>
          </div>

          {/* Right: CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:flex items-center gap-1.5 h-9 px-5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${scrolled ? 'bg-primary-500 text-background-50 hover:bg-primary-600' : 'bg-white text-foreground-950 hover:bg-accent-500'}`}
            >
              Buy It Now
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-sm"></i>
              </span>
            </a>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${scrolled ? 'text-foreground-950 hover:bg-foreground-100' : 'text-white hover:bg-white/15'}`}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          {/* Slide-out Panel */}
          <div className="absolute top-0 right-0 w-[85%] max-w-[360px] h-full bg-background-50 overflow-y-auto">
            <div className="p-5">
              {/* Close header */}
              <div className="flex items-center justify-between mb-6">
                <Link
                  to="/"
                  className="font-black font-heading text-lg text-foreground-950 whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Kakobuy Spreadsheet
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Mobile Nav Links */}
              <div className="space-y-1 mb-6">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-foreground-800 font-medium text-sm hover:bg-accent-100 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line"></i>
                  </span>
                  Home
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-foreground-800 font-medium text-sm hover:bg-accent-100 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-guide-line"></i>
                  </span>
                  Shopping Guide
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-foreground-800 font-medium text-sm hover:bg-accent-100 transition-colors cursor-pointer sm:hidden"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-bag-line"></i>
                  </span>
                  Buy It Now
                </a>
              </div>

              {/* Divider */}
              <div className="border-t border-background-200 my-4"></div>

              {/* Mobile Category Toggle */}
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-foreground-950 font-semibold text-sm cursor-pointer mb-2"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-apps-2-line"></i>
                  </span>
                  Browse Categories
                </span>
                <span className={`w-6 h-6 flex items-center justify-center transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {mobileCategoryOpen && (
                <div className="space-y-0.5 pl-4">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl text-foreground-700 text-sm hover:bg-accent-100 transition-colors cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}